<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type {
  InitialData as ChooseOptionStopDialogFormInitialData,
  ValidData as ChooseOptionStopDialogFormValidData,
} from '@/components/dialogForms/ChooseOptionStopDialogForm.vue';

import { computed, useTemplateRef } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';
import ChooseOptionStopDialogForm from '@/components/dialogForms/ChooseOptionStopDialogForm.vue';
import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';

export interface Props {
  isConfirmLoading?: boolean;
  confirm?: (formData: ChooseOptionStopDialogFormValidData) => Promise<unknown>;
  stopNum?: number;
}
export type DefProps = typeof props;
const props = withDefaults(defineProps<Props>(), {
  isConfirmLoading: false,
  confirm: undefined,
  stopNum: undefined,
});
const emit = defineEmits<{
  cancelBtnClick: [event: Event];
  confirmBtnClick: [
    event: SubmitEventPromise | null | undefined,
    formData: ChooseOptionStopDialogFormValidData,
    props: DefProps,
  ];
}>();

function handleCancelClick(event: Event): void {
  // also it should be wrapped with <v-dialog :persistent="isConfirmLoading"
  if (props.isConfirmLoading) return;
  emit('cancelBtnClick', event);
}

function handleSubmit(
  event: SubmitEventPromise | null | undefined,
  formData: ChooseOptionStopDialogFormValidData,
): void {
  if (props.isConfirmLoading) return;
  emit('confirmBtnClick', event, formData, props);
}

const mainEl = useTemplateRef('main');

function getSubmitBtn(): HTMLInputElement | null {
  if (!mainEl.value) return null;
  return mainEl.value.querySelector('input[type=submit]');
}

function handleConfirmClick(event: Event): void {
  const submitBtn = getSubmitBtn();
  if (!submitBtn) return;
  submitBtn.click();
}

const initialData = computed<ChooseOptionStopDialogFormInitialData>(() => {
  return {
    stopType: undefined,
  };
});
</script>

<template>
  <PageDialogWrapper
    :class="$style.chooseOptionStopDialog"
    data-testid="chooseOptionStopDialog"
  >
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        <template v-if="props.stopNum">Stop {{ props.stopNum }}</template>
        <template v-else>Job completion</template>
      </ReportGenerateDialogHeader>
    </template>
    <section ref="main" :class="$style.main">
      <div :class="$style.text">Please, choose the correct option.</div>
      <ChooseOptionStopDialogForm
        :stop-num="props.stopNum"
        :initial-data="initialData"
        :loading="props.isConfirmLoading"
        @submit="handleSubmit"
      />
    </section>
    <template #footer>
      <menu :class="$style.dialogFooterMenuList">
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            variant="outlined"
            append-icon="mdi-close"
            :class="$style.btn"
            @click="handleCancelClick"
          >
            Cancel
          </KBtn>
        </li>
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            append-icon="mdi-page-next-outline"
            :class="$style.btn"
            :loading="props.isConfirmLoading"
            :disabled="!props.confirm"
            @click="handleConfirmClick"
          >
            Next
          </KBtn>
        </li>
      </menu>
    </template>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use 'sass:math';
@use '@/styles/colors';
@use '@/styles/variables';

.chooseOptionStopDialog {
  width: variables.$reportGenerateDialogWidth;
}
  .main {
  }
  .text {
    margin: 0 0 math.div(variables.$pageDialogWindowMainWrapperMargin, 2) 0;
  }
  .dialogFooterMenuList {
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: right;
  }
    .dialogFooterMenuListItem {
      display: inline;
    }
    .dialogFooterMenuListItem + .dialogFooterMenuListItem {
      margin-left: 1rem;
    }
      .btn {
      }
</style>
